// User-defined chat prompts, addressable as slash commands.
//
// Each prompt is { command, title, body, updatedAt }. Stored in
// localStorage per authenticated tenant and user so a chat user can keep a personal library
// of reusable instructions — e.g. a "scan-trifecta" rule that tells
// the agent to fetch sessions and spawn_batch them against a system
// prompt.
//
// Slash command convention: lowercase, kebab-case, no leading "/".
// `/scan-trifecta` → command = "scan-trifecta". Names are sanitized
// on save; lookup is case-insensitive.
//
// This file is pure storage — UI and chat wiring live elsewhere.

import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";

const STORAGE_KEY_PREFIX = "originChat.userPrompts.v2";
const LEGACY_STORAGE_KEY_PREFIX = "originChat.userPrompts.v1";

function storageKey(tenantId) {
    if (typeof tenantId !== "string" || !tenantId.trim() || tenantId === "default") return null;
    return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

function discardLegacy(tenantId) {
    if (typeof localStorage === "undefined") return;
    discardLegacyTenantEntry(localStorage, LEGACY_STORAGE_KEY_PREFIX, tenantId);
}

const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try { fn(); } catch { /* listener errors don't block others */ }
    }
}

export function subscribePrompts(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function listPrompts(tenantId) {
    discardLegacy(tenantId);
    const key = storageKey(tenantId);
    if (!key) return [];
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((p) => p && typeof p.command === "string" && typeof p.body === "string")
            .sort((a, b) => a.command.localeCompare(b.command));
    } catch {
        return [];
    }
}

export function findPromptByCommand(tenantId, command) {
    if (!command) return null;
    const target = sanitizeCommand(command);
    if (!target) return null;
    return listPrompts(tenantId).find((p) => p.command === target) || null;
}

export function savePrompt(tenantId, { command, title, body }) {
    const key = storageKey(tenantId);
    if (!key) throw new Error("verified tenant and user identity are required");
    const cmd = sanitizeCommand(command);
    if (!cmd) throw new Error("invalid command name");
    if (!body || !body.trim()) throw new Error("body is required");
    const entry = {
        command: cmd,
        title: (title && title.trim()) || cmd,
        body: body.trim(),
        updatedAt: Date.now(),
    };
    const list = listPrompts(tenantId).filter((p) => p.command !== cmd);
    list.push(entry);
    list.sort((a, b) => a.command.localeCompare(b.command));
    localStorage.setItem(key, JSON.stringify(list));
    notify();
    return entry;
}

export function deletePrompt(tenantId, command) {
    const key = storageKey(tenantId);
    if (!key) throw new Error("verified tenant and user identity are required");
    const cmd = sanitizeCommand(command);
    if (!cmd) return;
    const list = listPrompts(tenantId).filter((p) => p.command !== cmd);
    localStorage.setItem(key, JSON.stringify(list));
    notify();
}

// Lowercase, dash-only. Drops a leading slash if present so callers
// can pass either form. Returns "" for inputs that can't sanitize
// into something safe to use as a slash command.
export function sanitizeCommand(raw) {
    if (!raw) return "";
    const trimmed = String(raw).trim().replace(/^\//, "");
    const slug = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return slug.slice(0, 64);
}

// Parse a leading-slash message into { command, args } if it looks
// like a slash command, else null. `args` is the trailing string
// (free-form, may be empty).
export function parseSlashInput(text) {
    if (!text) return null;
    const match = /^\/([a-z0-9][a-z0-9-_]*)\s*(.*)$/i.exec(text.trim());
    if (!match) return null;
    return { command: sanitizeCommand(match[1]), args: match[2] || "" };
}
